import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { Locale } from "@linkfluence/models/locale";
import { Timezone } from "@linkfluence/models/timezone";
import { Cluster } from "@radarly/models/insights";
import ImageClusterDetail from "./cluster-detail";
import ImageClustersGrid from "./cluster-grid";
import DetailHeader from "./detail-header";
import {
  FOOTER_CLUSTER_HEIGHT,
  FOOTER_GRID_HEIGHT,
  GRID_ROW_GAP,
  KEYWORDS_RECAP_HEIGHT,
  PICTURE_DETAIL_GAP,
  PICTURE_DETAIL_WIDTH,
  PICTURE_GRID_GAP,
  PICTURE_GRID_WIDTH,
  TILE_OUTER_SPACE,
} from "./dimensions";
import Footer from "./footer";
import { useClusterScroll } from "./useClusterScroll";

export interface IProps {
  clusters: Cluster[];
  clusterView: Cluster;
  locale: Locale;
  className?: string;
  timezone: Timezone;
  height: number;
  isFullscreen?: boolean;
  isReadOnly?: boolean;
  onClick?: (uids: string[]) => void;
  onChangeClusterView: (cluster: Cluster) => void;
}

const Wrapper = styled.div<{ height: number }>`
  overflow-y: hidden;
  height: ${({ height }) => `${height}px`};
`;

const Scroller = styled.div`
  width: 100%;
  transition: transform 0.7s;
  will-change: transform;
`;

const getTotalItems = (cluster: Cluster, clusters: Cluster[]): number => {
  if (cluster) {
    return cluster.hits.length;
  }
  return clusters.length;
};

const getLimit = (
  cluster: Cluster,
  scrollHeight: number,
  containerWidth: number,
  isFullscreen: boolean,
  detailImageFullscreenSize: number,
  gridImageFullscreenSize: number
): number => {
  if (cluster) {
    const detailWidth = isFullscreen
      ? detailImageFullscreenSize
      : PICTURE_DETAIL_WIDTH;
    const picturesPerRow = Math.floor(
      containerWidth / (detailWidth + PICTURE_DETAIL_GAP)
    );
    const rowsPerPage = Math.floor(scrollHeight / detailWidth);
    return picturesPerRow * rowsPerPage;
  }
  const gridWidth = isFullscreen ? gridImageFullscreenSize : PICTURE_GRID_WIDTH;
  return Math.floor(scrollHeight / (gridWidth + PICTURE_GRID_GAP));
};

const RadarlyWidgetImageClustering = ({
  clusters,
  locale,
  className,
  timezone,
  height,
  isFullscreen,
  onClick,
  clusterView,
  onChangeClusterView,
  isReadOnly,
}: IProps) => {
  const [fullScreenHeight, setFullscreenHeight] = useState(0);
  const ref = useRef(null);
  const currentHeight = isFullscreen
    ? fullScreenHeight - TILE_OUTER_SPACE
    : height;

  useLayoutEffect(() => {
    if (!!document.fullscreenElement) {
      setFullscreenHeight(window.innerHeight);
    }
  }, [fullScreenHeight]);

  const onClickView = useCallback(() => {
    if (isFullscreen && !clusterView) {
      return onClick(
        clusters.flatMap((cluster) => cluster.hits.map((hit) => hit.uid))
      );
    }
    return onClick(clusterView.hits.map((hit) => hit.uid));
  }, [clusterView, clusters, isFullscreen, onClick]);

  const pageHeight =
    clusterView || isFullscreen
      ? currentHeight - FOOTER_CLUSTER_HEIGHT
      : currentHeight - FOOTER_GRID_HEIGHT;
  const scrollHeight = clusterView
    ? pageHeight - KEYWORDS_RECAP_HEIGHT
    : pageHeight;
  const detailImageFullscreenSize = scrollHeight / 3 - PICTURE_DETAIL_GAP; // 3 rows in fullscreen mode
  const gridImageFullscreenSize = scrollHeight / 5 - GRID_ROW_GAP; // 5 rows in fullscreen mode
  const totalItems = getTotalItems(clusterView, clusters);
  const limit = useMemo(
    () =>
      getLimit(
        clusterView,
        scrollHeight,
        ref?.current?.clientWidth,
        isFullscreen,
        detailImageFullscreenSize,
        gridImageFullscreenSize
      ),
    [
      clusterView,
      scrollHeight,
      isFullscreen,
      detailImageFullscreenSize,
      gridImageFullscreenSize,
    ]
  );

  const {
    gridScroll,
    detailScroll,
    gridPage,
    detailPage,
    onPageUp,
    onPageDown,
    onResetDetailScroll,
  } = useClusterScroll(scrollHeight, totalItems, limit);

  const onChangeView = (cluster: Cluster) => {
    onChangeClusterView(cluster);
    if (!cluster) {
      onResetDetailScroll();
    }
  };

  return (
    <div className={className}>
      <Wrapper height={pageHeight} ref={ref}>
        {clusterView ? (
          <div>
            <DetailHeader
              cluster={clusterView}
              onClickBack={() => onChangeView(null)}
              locale={locale}
            />
            <Scroller
              style={{
                transform: `translateY(${detailScroll}px)`,
              }}
            >
              <ImageClusterDetail
                cluster={clusterView}
                locale={locale}
                timezone={timezone}
                isFullscreen={isFullscreen}
                detailImageFullscreenSize={detailImageFullscreenSize}
              />
            </Scroller>
          </div>
        ) : (
          <Scroller style={{ transform: `translateY(${gridScroll}px)` }}>
            <ImageClustersGrid
              clusters={clusters}
              onOpenCluster={(cluster: Cluster) => onChangeView(cluster)}
              locale={locale}
              isFullscreen={isFullscreen}
              gridImageFullscreenSize={gridImageFullscreenSize}
            />
          </Scroller>
        )}
      </Wrapper>
      <Footer
        isFullscreen={isFullscreen}
        page={clusterView ? detailPage : gridPage}
        onPageUp={() => onPageUp(clusterView ? "detail" : "grid")}
        onPageDown={() => onPageDown(clusterView ? "detail" : "grid")}
        onClick={!isFullscreen && isReadOnly ? null : onClickView}
        locale={locale}
        totalItems={totalItems}
        showPagination={totalItems > limit}
        isDetailView={!!clusterView}
      />
    </div>
  );
};

export default RadarlyWidgetImageClustering;
